<template>
  <div class="cropping-canvas">
    <cropping-canvas-toolbar
      :can-change-ratio="!hideAspectRatio"
      :can-rotate="!disableRotate"
      :can-undo="!disableUndo"
      :can-reset="!disableReset"
      :can-zoom="!disableZoom"
      :zoom-data="zoomData"
      :selected-aspect-ratio="selectedAspectRatio"
      @crop-action="handleCropAction"
    />
    <div class="canvas-container">
      <canvas id="canvas" style="display: none"></canvas>
      <img id="image" class="source-image" style="display: none" :src="imgSrc" @load="init" />
      <span v-if="showPixelTag" class="pixel-tag" :style="pixelTagCSS">{{ pixelTag }}</span>
      <div v-if="!disableZoom" class="zoom-actions">
        <button
          type="button"
          class="btn-zoom-default"
          @click="handleCropAction(CROP_ACTION.ZOOM_DEFAULT)"
        >
          <span class="zoom-label">{{ zoomSizeLabel }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Cropper from "cropperjs";
import DOMUtils from "@utils/dom.utils";
import drawingUtils from "@plugins/drawing/drawing.utils";
import {
  CROP_ACTION,
  CroppingToolKind,
  IMAGE_DIMENSION_LIMIT,
  VIEW_MODE,
  wheelZoomRatio,
  zoomRatio,
} from "@plugins/cropping/cropping.constant";
import { isEmpty } from "@utils/var.check";
import CroppingCanvasToolbar from "@plugins/cropping/components/CroppingCanvasToolbar.vue";
import { debounceFn } from "@/utils/debounce";
import { computed, onMounted, ref, watch } from "vue";
import { useVm } from "@utils/vm.ts";
import { useI18n } from "vue-i18n";

// Props
interface Props {
  file?: File;
  kind?: string;
  fixedCropSize?: Record<string, unknown>;
  exportSize?: Record<string, unknown>;
  initialAspectRatio?: number;
  cropTask?: string | null;
}

const {
  file,
  kind = "basic",
  fixedCropSize = null,
  exportSize = null,
  initialAspectRatio = 0,
  cropTask = null,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  crop: [data: unknown];
}>();

// Composables
const { vm } = useVm();
const { t } = useI18n();

// Data
const imgSrc = ref("");
const cropper = ref<Cropper>();
const image = ref<HTMLImageElement | undefined>(undefined);
const cropBoxData = ref<Cropper.CropBoxData | undefined>(undefined);
const canvasData = ref<Cropper.CanvasData | undefined>(undefined);
const imageData = ref<Cropper.Data | undefined>(undefined);
const containerData = ref<Cropper.ContainerData | undefined>(undefined);
const zoomData = ref(0);
const aspectRatio = ref(0);
const minContainerHeight = ref(400);
const undoStack = ref([]);
const selectedAspectRatio = ref("original");
const minContainerWidth = ref(0);
const baseOnHeight = ref(false);

// Computed
const hideAspectRatio = computed(() => {
  return CroppingToolKind[kind]?.hideAspectRatio;
});

const disableResize = computed(() => {
  return CroppingToolKind[kind]?.disableResize;
});

const disableRotate = computed(() => {
  return CroppingToolKind[kind]?.disableRotate;
});

const disableUndo = computed(() => {
  return CroppingToolKind[kind]?.disableUndo;
});

const disableReset = computed(() => {
  return CroppingToolKind[kind]?.disableReset;
});

const disableZoom = computed(() => {
  return CroppingToolKind[kind]?.disableZoom;
});

const enableWhitening = computed(() => {
  return CroppingToolKind[kind]?.enableWhitening;
});

const viewMode = computed(() => {
  const viewModes = Object.values(VIEW_MODE);
  const viewMode = CroppingToolKind[kind]?.viewMode;
  const defaultViewMode = VIEW_MODE.NO_RESTRICTION;
  return viewModes.includes(viewMode) ? viewMode : defaultViewMode;
});

const showPixelTag = computed(() => {
  return !isEmpty(cropBoxData.value) && !exportSize;
});

const pixelTag = computed(() => {
  const width = imageData.value?.width ?? 0;
  const height = imageData.value?.height ?? 0;
  return `${Math.floor(width)} x ${Math.floor(height)}`;
});

const pixelTagCSS = computed(() => {
  const offset = 8;
  const pixelTagWidth = DOMUtils.el(".pixel-tag") ? DOMUtils.el(".pixel-tag").offsetWidth : 100;
  const pixelTagHeight = 30;
  const cropLeft = cropBoxData.value?.left ?? 0;
  const cropTop = cropBoxData.value?.top ?? 0;
  const cropWidth = cropBoxData.value?.width ?? 0;
  const cropHeight = cropBoxData.value?.height ?? 0;
  // default show on the right
  let x = cropLeft + cropWidth + offset;
  let y = cropTop + cropHeight / 2 - pixelTagHeight / 2;

  // not enough space on the right -> show at the bottom
  if (x + pixelTagWidth >= (containerData.value?.width ?? 0)) {
    x = cropLeft + cropWidth / 2 - pixelTagWidth / 2;
    y = cropTop + cropHeight + offset;
  }
  // not enough space at the bottom -> show at the top
  if (y + pixelTagHeight >= (containerData.value?.height ?? 0)) {
    y = cropTop - (pixelTagHeight + offset);
  }
  // no space on top & bottom -> show on the left
  if (y < 0) {
    x = cropLeft - pixelTagWidth - offset;
    y = cropTop + cropHeight / 2 - pixelTagHeight / 2;
  }

  return `transform: translate(${x}px,${y}px)`;
});

const hasFixedCropSize = computed(() => {
  return fixedCropSize?.width && fixedCropSize?.height;
});

const hasExportSize = computed(() => {
  return exportSize?.width && exportSize?.height;
});

const zoomSizeLabel = computed(() => {
  return t("tinymce_plugin_drawing_modal_zoom_size_label", {
    zoomSize: Math.round(zoomData.value * 100),
  });
});

const zoomOutLimit = computed(() => {
  // For crop inside canvas, the size of cropped area should not exceed canvas size
  return (
    viewMode.value === VIEW_MODE.CROP_INSIDE_CANVAS &&
    ((imageData.value?.width ?? 0) >= (canvasData.value?.naturalWidth ?? 0) ||
      (imageData.value?.height ?? 0) >= (canvasData.value?.naturalHeight ?? 0))
  );
});

// Methods
const setCanvasSize = () => {
  const canvasContainer = DOMUtils.elOf(vm.$el, ".canvas-container")[0];
  const containerMargin =
    parseInt(window.getComputedStyle(canvasContainer).marginTop) +
    parseInt(window.getComputedStyle(canvasContainer).marginBottom);
  minContainerHeight.value =
    DOMUtils.parent(vm.$el, ".bx--modal-content").clientHeight - containerMargin - 32;
  minContainerWidth.value = canvasContainer.getBoundingClientRect().width;
};

const setBaseOnHeight = () => {
  const imageHeight = image.value?.naturalHeight ?? 0;
  const imageWidth = image.value?.naturalWidth ?? 0;
  baseOnHeight.value =
    minContainerWidth.value / minContainerHeight.value > imageWidth / imageHeight;
};

const handleCropAction = (action, value?) => {
  switch (action) {
    case CROP_ACTION.ASPECT_RATIO: {
      setCropRatio(value);
      break;
    }
    case CROP_ACTION.ROTATE: {
      rotate(-90);
      break;
    }
    case CROP_ACTION.UNDO: {
      undo();
      break;
    }
    case CROP_ACTION.CLEAR: {
      reset();
      break;
    }
    case CROP_ACTION.ZOOM_IN: {
      zoom(zoomRatio);
      break;
    }
    case CROP_ACTION.ZOOM_OUT: {
      zoom(-zoomRatio);
      break;
    }
    case CROP_ACTION.ZOOM_DEFAULT: {
      setZoomAccordingToImage();
      break;
    }
  }
};

const reset = () => {
  cropper.value?.reset();
  setZoomAccordingToImage();
  aspectRatio.value = initialAspectRatio !== 0 ? initialAspectRatio : calculateOriginalRatio();
  selectedAspectRatio.value = "original";
  cropper.value?.setData({
    x: 0,
    y: 0,
    width: image.value?.naturalWidth,
    height: image.value?.naturalHeight,
  });
  cropper.value?.setAspectRatio(aspectRatio.value);
  initCropBox();
  cropBoxData.value = cropper.value?.getCropBoxData();
  canvasData.value = cropper.value?.getCanvasData();
  imageData.value = cropper?.value?.getData(true);
  containerData.value = cropper.value?.getContainerData();
  calculateZoomLevel();
  undoStack.value = [];
};

const initCropBox = () => {
  if (hasFixedCropSize.value) {
    const { width, height } = calculateCropBoxDisplayFixedCropSize();
    const { top, left } = calculateCenterPosition({ width, height });

    const updatedCropBoxData = {
      ...cropper.value?.getCropBoxData(),
      width,
      height,
      top,
      left,
    };
    setCropBoxData(updatedCropBoxData);
  }
};

const calculateCropBoxDisplayFixedCropSize = () => {
  // set cropbox's corresponding size in screen
  const {
    width: canvasWidth,
    height: canvasHeight,
    naturalWidth,
    naturalHeight,
  } = cropper.value.getCanvasData();
  const { width: cropBoxWidth, height: cropBoxHeight } = fixedCropSize;
  // calculate crop size in screen using display ratio canvas:natural
  const width = (cropBoxWidth * canvasWidth) / naturalWidth;
  const height = (cropBoxHeight * canvasHeight) / naturalHeight;
  return { width, height };
};

const calculateCenterPosition = ({ width, height }) => {
  // set cropbox's position to center
  const {
    top: canvasTop,
    left: canvasLeft,
    width: canvasWidth,
    height: canvasHeight,
  } = cropper.value.getCanvasData();
  let top = canvasHeight / 2 - height / 2;
  top += canvasTop;
  let left = canvasWidth / 2 - width / 2;
  left += canvasLeft;
  return { top, left };
};

/**
 * Zoom the canvas with a relative ratio
 * @param {number} ratio - The target ratio.
 * @param {Event} _originalEvent - The original event if any.
 * @returns {Object} this
 */
const zoom = (ratio) => {
  if (disableZoom.value) return;
  if (ratio < 0 && zoomOutLimit.value) return;
  addUndoEvent("zoom", cropper.value?.getCanvasData());
  // return cropper.value.zoomTo(zoomData.value + ratio);
  return cropper.value?.zoom(ratio);
};

/**
 * Rotate the canvas with a relative degree
 * @param {number} degree - The rotate degree.
 * @returns {Object} this
 */
const rotate = (degree) => {
  if (disableRotate.value) return;
  addUndoEvent("rotate", degree);
  return cropper.value?.rotate(degree);
};

const undo = () => {
  //eventType: zoom/rotate/selectionChange/ratioChange

  if (undoStack.value.length === 0) return;

  const history = undoStack.value.pop();
  switch (history.event) {
    case "zoom":
      cropper.value.setCanvasData(history.data);
      canvasData.value = cropper.value.getCanvasData();
      zoomData.value =
        Math.round((canvasData.value.height / canvasData.value.naturalHeight) * 100) / 100;
      break;
    case "rotate":
      cropper.value.rotate(-1 * history.data);
      break;
    case "selectionChange":
      cropper.value.setCropBoxData(history.data);
      cropBoxData.value = cropper.value.getCropBoxData();
      break;
    case "ratioChange":
      cropper.value.setAspectRatio(history.data.ratioValue);
      selectedAspectRatio.value = history.data.ratio;
      cropper.value.setCropBoxData(history.data.cropBoxData);
      cropBoxData.value = cropper.value.getCropBoxData();
      break;
    default:
      break;
  }

  cropBoxData.value = cropper.value.getCropBoxData();
};

const addUndoEvent = (eventType, data) => {
  undoStack.value.push({
    event: eventType,
    data: data,
  });
};

/**
 * Set the crop box position and size with new data.
 * @param {Object} data - The new crop box data.
 * @returns {Cropper} this
 */
const setCropBoxData = (data) => {
  if (!data) return;
  return cropper.value.setCropBoxData(data);
};

const setCropRatio = (ratio) => {
  const cropBoxData = cropper.value.getCropBoxData();
  const data = {
    cropBoxData: cropBoxData,
    ratio: selectedAspectRatio.value,
    ratioValue: aspectRatio.value,
  };
  addUndoEvent("ratioChange", data);

  selectedAspectRatio.value = ratio;
  if (ratio === "original") {
    aspectRatio.value = calculateOriginalRatio();
  } else {
    aspectRatio.value = ratio.split("/").reduce((p, c) => p / c);
  }

  cropper.value.setAspectRatio(aspectRatio.value);

  // Restore cropbox dimension & position for freeform mode
  if (ratio === "0") {
    setCropBoxData(cropBoxData);
  }

  cropBoxData.value = cropper.value.getCropBoxData();
  imageData.value = cropper.value.getData();
};

const calculateOriginalRatio = () => {
  const gcd = (a, b) => (b ? gcd(b, a % b) : a);
  const divisor = gcd(image.value.naturalWidth, image.value.naturalHeight);
  return image.value.naturalWidth / divisor / (image.value.naturalHeight / divisor);
};

const setZoomAccordingToImage = () => {
  if (baseOnHeight.value) {
    cropper.value.zoomTo(minContainerHeight.value / image.value.naturalHeight);
  } else {
    cropper.value.zoomTo(minContainerWidth.value / image.value.naturalWidth);
  }
};

const calculateZoomLevel = () => {
  if (baseOnHeight.value) {
    const num = minContainerHeight.value / image.value.naturalHeight;
    zoomData.value = Math.round(num * 100) / 100;
  } else {
    const num = minContainerWidth.value / image.value.naturalWidth;
    zoomData.value = Math.round(num * 100) / 100;
  }
};

const generateWhitenOriginalImage = () => {
  imageData.value = cropper.value.getData(true);
  const canvas = document.createElement("canvas");
  canvas.width = image.value.naturalWidth;
  canvas.height = image.value.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image.value, 0, 0);
  ctx.fillStyle = "white";
  ctx.fillRect(imageData.value.x, imageData.value.y, imageData.value.width, imageData.value.height);
  const base64 = canvas.toDataURL(file.type);
  const blob = drawingUtils.serializeBase64ToBlob(base64, {
    fileType: file.type,
  });
  return new File([blob], file.name, {
    type: blob.type,
  });
};

const cropImage = async () => {
  // get image data for post processing, e.g. upload or setting image src
  imageData.value = cropper.value.getData();
  // do our own rounding as cropperjs will add 1px in certain case
  // https://github.com/fengyuanchen/cropperjs/issues/343
  imageData.value.x = Math.floor(imageData.value.x);
  imageData.value.y = Math.floor(imageData.value.y);
  imageData.value.width = Math.floor(imageData.value.width);
  imageData.value.height = Math.floor(imageData.value.height);

  const isSvg = file.type.includes("image/svg+xml");
  const fileType = isSvg ? "image/png" : file.type;

  let cropConfig = {
    fillColor: "#fff",
    maxWidth: IMAGE_DIMENSION_LIMIT.WIDTH,
    maxHeight: IMAGE_DIMENSION_LIMIT.HEIGHT,
  };
  if (hasExportSize.value) {
    cropConfig = {
      ...cropConfig,
      width: exportSize.width,
      height: exportSize.height,
    };
  }

  const croppedCanvas = cropper.value.getCroppedCanvas(cropConfig);
  const base64 = croppedCanvas.toDataURL(fileType, 1);

  const blob = drawingUtils.serializeBase64ToBlob(base64, {
    fileType: file.type,
  });

  const croppedFile = new File([blob], file.name, {
    type: blob.type,
  });

  emit("crop", {
    file: croppedFile,
    whitenFile: enableWhitening.value ? generateWhitenOriginalImage() : null,
    imageData: imageData.value,
    base64: base64,
  });
};

const init = () => {
  image.value = vm.$el.querySelector(".source-image");
  setCanvasSize();
  setBaseOnHeight();

  const debounceZoomOnScroll = debounceFn(
    () => addUndoEvent("zoom", cropper.value.getCanvasData()),
    500,
    {
      leading: true,
      trailing: false,
    },
  );

  cropper.value = new Cropper(image.value, {
    viewMode: viewMode.value,
    minContainerHeight: minContainerHeight.value,
    zoomable: !disableZoom.value,
    wheelZoomRatio: disableZoom.value ? wheelZoomRatio : 0,
    autoCropArea: 1,
    cropBoxResizable: !disableResize.value,
    dragMode: disableResize.value ? "none" : "crop",
    guides: false,
    center: false,
    ready: () => {
      reset();
    },
    cropmove: () => {
      cropBoxData.value = cropper.value.getCropBoxData();
      imageData.value = cropper.value.getData();
    },
    cropstart: () => {
      cropBoxData.value = cropper.value.getCropBoxData();
      addUndoEvent("selectionChange", cropBoxData.value);
    },
    crop: () => {
      imageData.value = cropper.value.getData();
    },
    zoom: (event) => {
      if (event.detail.ratio < event.detail.oldRatio && zoomOutLimit.value) return;
      canvasData.value = cropper.value.getCanvasData();
      imageData.value = cropper.value.getData();
      zoomData.value = Math.round(event.detail.ratio * 100) / 100;

      if (event.detail.originalEvent) {
        debounceZoomOnScroll();
      }
    },
  });
};

// Watch
watch(
  () => cropTask,
  (newValue) => {
    if (newValue === "crop") {
      cropImage();
    }
  },
);

// Lifecycle
onMounted(() => {
  imgSrc.value = URL.createObjectURL(file);
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.cropping-canvas {
  @include media("tablet-min") {
    display: flex;
    position: relative;
  }

  .canvas-container {
    position: relative;
    overflow: hidden;

    @include media("tablet-min") {
      flex: 1 1 0%;
      margin: 3.5rem 0 $spacing-05;
    }

    > .zoom-actions {
      @include media("tablet-min") {
        display: none;
      }
      top: 1rem;
      right: 1rem;
      width: 6rem;
      padding: $spacing-04 $spacing-05;
      background-color: $ui-background;
      text-align: center;
      box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);
      border-radius: 10px;
    }
  }

  .pixel-tag {
    position: absolute;
    left: 0;
    top: 0;
    padding: $spacing-02 $spacing-03;
    background-color: $field-02;
    border-radius: 10px;
  }

  /* There are two zoom-actions, one in CroppingCanvas and another in CroppingCanvasToolbar
   * This is the shared styling for both of them
   */

  .zoom-actions {
    position: absolute;
    z-index: z-map("default");

    .sls-icon {
      margin-right: $spacing-03;

      [fill] {
        fill: $interactive-01;
      }
    }

    .btn-zoom-in {
      margin-right: $spacing-05;
    }

    .btn-zoom-default {
      @include carbon--type-style("label-03", true);
      color: $text-02;
    }
  }
}

// CropperJS styling
.cropper-container {
  direction: ltr;
  font-size: 0;
  line-height: 0;
  position: relative;
  touch-action: none;
  user-select: none;
  max-width: 100%;

  & img {
    backface-visibility: hidden;
    display: block;
    height: 100%;
    image-orientation: 0deg;
    max-height: none;
    max-width: none;
    min-height: 0;
    min-width: 0;
    width: 100%;
    background-color: $ui-01;
  }
}

.cropper-wrap-box,
.cropper-canvas,
.cropper-drag-box,
.cropper-crop-box,
.cropper-modal {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.cropper-wrap-box,
.cropper-canvas {
  overflow: hidden;
}

.cropper-drag-box {
  background-color: #fff;
  opacity: 0;
}

.cropper-modal {
  background-color: $overlay-03;
  opacity: 1;
  border-radius: 10px;
}

.cropper-view-box {
  display: block;
  height: 100%;
  outline: 1px solid #fff;
  overflow: hidden;
  width: 100%;
  background-color: $ui-01;
}

.cropper-dashed {
  border: 0 dashed #eee;
  display: block;
  opacity: 0.5;
  position: absolute;

  &.dashed-h {
    border-bottom-width: 1px;
    border-top-width: 1px;
    height: calc(100% / 3);
    left: 0;
    top: calc(100% / 3);
    width: 100%;
  }

  &.dashed-v {
    border-left-width: 1px;
    border-right-width: 1px;
    height: 100%;
    left: calc(100% / 3);
    top: 0;
    width: calc(100% / 3);
  }
}

.cropper-center {
  display: block;
  height: 0;
  left: 50%;
  opacity: 0.75;
  position: absolute;
  top: 50%;
  width: 0;

  &::before,
  &::after {
    background-color: #eee;
    content: " ";
    display: block;
    position: absolute;
  }

  &::before {
    height: 1px;
    left: -3px;
    top: 0;
    width: 7px;
  }

  &::after {
    height: 7px;
    left: 0;
    top: -3px;
    width: 1px;
  }
}

.cropper-face,
.cropper-line,
.cropper-point {
  display: block;
  height: 100%;
  opacity: 0.1;
  position: absolute;
  width: 100%;
}

.cropper-face {
  background-color: #fff;
  left: 0;
  top: 0;
}

.cropper-line {
  &.line-e {
    cursor: ew-resize;
    right: -3px;
    top: 0;
    width: 5px;
  }

  &.line-n {
    cursor: ns-resize;
    height: 5px;
    left: 0;
    top: -3px;
  }

  &.line-w {
    cursor: ew-resize;
    left: -3px;
    top: 0;
    width: 5px;
  }

  &.line-s {
    bottom: -3px;
    cursor: ns-resize;
    height: 5px;
    left: 0;
  }
}

.cropper-point {
  background-color: #fff;
  height: 3px;
  width: 3px;
  opacity: 1;

  &.point-e {
    cursor: ew-resize;
    right: -3px;
    top: 50%;
    transform: translateY(-50%);
    height: 32px;
  }

  &.point-n {
    cursor: ns-resize;
    left: 50%;
    top: -3px;
    transform: translateX(-50%);
    width: 32px;
  }

  &.point-w {
    cursor: ew-resize;
    left: -3px;
    top: 50%;
    transform: translateY(-50%);
    height: 32px;
  }

  &.point-s {
    bottom: -3px;
    cursor: s-resize;
    left: 50%;
    transform: translateX(-50%);
    width: 32px;
  }

  &.point-ne {
    cursor: nesw-resize;
    right: -3px;
    top: -3px;
    width: 32px;

    &::before {
      content: "";
      display: block;
      height: 32px;
      width: 3px;
      position: absolute;
      right: 0;
      top: 0;
      background-color: $ui-01;
    }
  }

  &.point-nw {
    cursor: nwse-resize;
    left: -3px;
    top: -3px;
    height: 32px;

    &::before {
      content: "";
      display: block;
      width: 32px;
      height: 3px;
      position: absolute;
      left: 0;
      top: 0;
      background-color: #fff;
    }
  }

  &.point-sw {
    bottom: -3px;
    cursor: nesw-resize;
    left: -3px;
    height: 32px;

    &::before {
      content: "";
      display: block;
      width: 32px;
      height: 3px;
      position: absolute;
      left: 0;
      bottom: 0;
      background-color: #fff;
    }
  }

  &.point-se {
    bottom: -3px;
    cursor: nwse-resize;
    height: 32px;
    right: -3px;

    &::before {
      content: "";
      display: block;
      width: 32px;
      height: 3px;
      position: absolute;
      right: 0;
      bottom: 0;
      background-color: #fff;
    }
  }
}

.cropper-invisible {
  opacity: 0;
}

.cropper-hide {
  display: block;
  height: 0;
  position: absolute;
  width: 0;
}

.cropper-hidden {
  display: none !important;
}

.cropper-move {
  cursor: move;
}

.cropper-crop {
  cursor: crosshair;
}

.cropper-disabled .cropper-drag-box,
.cropper-disabled .cropper-face,
.cropper-disabled .cropper-line,
.cropper-disabled .cropper-point {
  cursor: not-allowed;
}
</style>
